<template>
  <div class="mod" :class="{ hoverable, compact }">
    <h2 v-if="modTitle && !compact">{{ modTitle }}</h2>
    <div>
      <div>
        <Icon :mod="mod" :size="compact ? 'medium' : ''" />
      </div>
      <section :class="$style.stats">
        <h2 v-if="!compact">PRIMARY STAT</h2>
        <div v-if="primary">
          {{ primary.value }}{{ primary.unitSign }} {{ primary.name }}

          <span
            v-if="primary.change"
            :class="['change', rateChange(primary.change)]"
          >
            {{ primary.change }}</span
          >
        </div>
        <div v-else>No primary</div>
        <h2 v-if="!compact">SECONDARY STATS</h2>
        <ol>
          <li
            v-for="(secondary, index) in secondaries"
            :key="index"
            :class="rate(secondary)"
            :style="progress(secondary)"
            :title="extremes(secondary)"
          >
            {{ secondary.value }}{{ secondary.unitSign }} {{ secondary.name }}
            <span v-if="!secondary.change">({{ secondary.roll }})</span>
            <span
              v-if="secondary.change"
              :class="['change', rateChange(secondary.change)]"
              >{{ secondary.change }}</span
            >
          </li>
          <li v-if="compact && secondaries.length == 0">No secondaries</li>
          <li
            v-if="average"
            :class="rate(average)"
            :style="progress(average)"
            :title="extremes(average)"
          >
            {{ average.value }}{{ average.unitSign }} {{ average.name }} ({{
              average.roll
            }})
          </li>
        </ol>
      </section>
    </div>
  </div>
</template>

<script>
import Icon from "./Icon.vue";
import { calcPercentage, statsToObject, getMinMax } from "/@/utils";

export default {
  name: "Mod",
  components: { Icon },
  props: {
    mod: Object,
    index: Number,
    hoverable: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      observer: undefined,
    };
  },
  computed: {
    average() {
      const secondaries = this.mod.secondaryStat;
      if (!secondaries) {
        return 0;
      }
      const totalPerc = secondaries
        .map((stat) => calcPercentage(stat, this.mod.pips))
        .reduce((a, b) => a + b, 0);
      const totalRolls = secondaries
        .map(({ roll }) => roll - 1)
        .reduce((a, b) => a + b, 0);
      return statsToObject([
        "Average",
        0,
        (totalPerc / secondaries.length) * 100,
        totalRolls,
      ]);
    },
    modTitle() {
      if (!this.make) {
        return "";
      }
      return `${this.make} ${this.mod.set_name} ${this.slot}`.toUpperCase();
    },
    make() {
      if (!this.mod.pips) {
        return "";
      }
      let roman = ["I", "II", "III", "IV", "V", "VI"];
      return `MK ${roman[this.mod.pips - 1]}`;
    },
    slot() {
      let slotName = [
        "Transmitter",
        "Receiver",
        "Processor",
        "Holo-Array",
        "Data-Bus",
        "Multiplexer",
      ];
      return slotName[this.mod.slot];
    },
    primary() {
      if (!this.mod.primaryStat) {
        return undefined;
      }
      return {
        ...this.mod.primaryStat,
        change: this.change[this.mod.primaryStat.name],
      };
    },
    secondaries() {
      return (this.mod.secondaryStat || []).map((secondary) => ({
        ...secondary,
        change: this.change[secondary.name],
      }));
    },
    change() {
      return this.mod.change || {};
    },
  },
  methods: {
    rate(secondary) {
      if (secondary.change) {
        return undefined;
      }
      const percentage = calcPercentage(secondary, this.mod.pips);
      if (percentage > 0.7) {
        return "good";
      }
      if (percentage < 0.3) {
        return "bad";
      }

      return "average";
    },
    rateChange(change) {
      if (change.startsWith("-")) {
        return "bad";
      }
      if (change == "0" || change == "+0") {
        return "average";
      }
      return "good";
    },
    progress(secondary) {
      const percentage = (
        calcPercentage(secondary, this.mod.pips) * 100
      ).toFixed(0);
      if (percentage == 100) {
        return { "background-color": "var(--fill-color)" };
      }
      return {
        background: `linear-gradient(108deg, var(--fill-color) 0% ${
          percentage - 1
        }%, ${percentage}%, var(--bg-color) ${percentage + 1}% 100%)`,
      };
    },
    extremes(secondary) {
      const [min, max] = getMinMax(secondary, this.mod.pips);
      return `Range: ${min}${secondary.unitSign}-${max}${secondary.unitSign}`;
    },
  },
  mounted() {
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry && entry.isIntersecting) {
        this.$emit("intersected", this.index);
      }
    });
    this.observer.observe(this.$el);
  },
  unmounted() {
    if (!this.observer) {
      return;
    }
    this.observer.disconnect();
  },
};
</script>

<style lang="scss" scoped>
.mod {
  flex: 1 1 20%;
  border: 3px solid aquamarine;
  margin: 5px;
  text-align: center;
  padding: 15px;
  background-color: #3d546df0;

  &.compact {
    border: 0;
    padding: 5px;
    font-size: 13px;

    h2 {
      font-size: small;
    }

    > h2:first-of-type {
      height: 1rem;
    }
  }

  & > div {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;

    & > * {
      flex: 1 0 auto;
    }
  }
}

.mod:hover {
  cursor: pointer;
}

.hoverable:hover {
  transform: scale(1.1);
}

.mod ol {
  list-style: none;
  padding: 0;
}

.mod li {
  border-radius: 2rem;
  margin: 0.3rem;
}

.mod h2 {
  font-size: medium;
}
.mod > h2:first-of-type {
  height: 3rem;
}

.good {
  font-weight: bold;
  --fill-color: green;
  --bg-color: #{scale-color(green, $lightness: 30%)};
}
.average {
  --fill-color: cornflowerblue;
  --bg-color: #{scale-color(cornflowerblue, $lightness: 30%)};
}

.bad {
  --fill-color: saddlebrown;
  --bg-color: #{scale-color(saddlebrown, $lightness: 30%)};
}

.change {
  background-color: grey;
  color: var(--bg-color);
  font-size: smaller;
  border-radius: 0.6rem;
  padding: 0 0.5rem;
}
</style>

<style lang="scss" module>
.stats {
    min-width: 25ch;
}
</style>
